import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRationing } from '../../context/RationingContext';
import { Link, Navigate } from 'react-router-dom';
import * as userService from '../../services/userService';
import { RationingCard, UnitCard } from '../../components/cards';

function RationingPage() {
  const { user, unit: authUnit, hasRole, loading: authLoading } = useAuth();
  const { rationingInfo, loading: rationingLoading, error: rationingError, refreshRationingData } = useRationing();
  const [loading, setLoading] = useState(true);
  const [unit, setUnit] = useState(null);
  const [error, setError] = useState(null);

  // Check if user has any staff role
  const isStaff = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));

  // Redirect staff users directly to admin dashboard
  if (user && isStaff && !authLoading) {
    return <Navigate to="/admin" replace />;
  }

  // Fetch unit information using endpoint
  useEffect(() => {
    const fetchUnitInfo = async () => {
      if (user && user.siteUuid && user.unitId) {
        try {
          const unitInfo = await userService.getUnitByUnitId(user.siteUuid, user.unitId);
          if (unitInfo) {
            setUnit(unitInfo);
            // console.log('Unit information fetched:', unitInfo);
          }
        } catch (error) {
          // console.error('Failed to fetch unit information:', error);
          setError('Failed to fetch unit information.');
        }
      } else if (authUnit) {
        setUnit(authUnit);
      }
      setLoading(false);
    };

    fetchUnitInfo();
  }, [user, authUnit]);

  // Show loading indicator if auth is still loading
  if (authLoading || rationingLoading || loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading data...</div>
      </div>
    );
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold mb-4">Please log in to view your rationing information</h2>
        <div className="flex space-x-4">
          <Link to="/login" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
            Sign In
          </Link>
          <Link to="/register" className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded">
            Create Account
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-hooray-red font-mont">Electricity Rationing</h1>
        <p className="text-hooray-red mt-2">
          View your current electricity rationing status and allocation.
        </p>
      </div>

      {(error || rationingError) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                {error || rationingError}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Rationing Information Card */}
        <RationingCard 
          rationStatus={rationingInfo.rationStatus}
          appliedRation={rationingInfo.appliedRation}
          remainingRation={rationingInfo.remainingRation}
          lastUpdated={rationingInfo.lastUpdated}
          percentRemaining={rationingInfo.percentRemaining}
        />

        {/* Unit Information Card */}
        <UnitCard unit={unit} isPrepaid={unit?.isPrepaid} />
      </div>

      {/* Additional Information Section */}
      <div className="card bg-white shadow-md rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 font-mont">About Electricity Rationing</h2>
        <div className="prose max-w-none">
          <p>
            Electricity rationing is a system implemented to manage electricity usage during periods 
            of high demand or limited supply. When active, your unit will be allocated a specific 
            amount of electricity that can be consumed within a set period.
          </p>
          <h3 className="font-semibold mt-4 font-mont">Understanding Rationing Status</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="font-semibold text-yellow-600">ACTIVE</span>: Rationing is currently 
              in effect. Your electricity usage is limited to a specific allocation. Monitor your usage carefully
              to avoid running out.
            </li>
            <li>
              <span className="font-semibold text-green-600">SUSPENDED</span>: Good news! Rationing is not 
              currently in effect. You can use electricity normally according to your account terms.
            </li>
            <li>
              <span className="font-semibold text-red-600">EXHAUSTED</span>: Your allocated ration 
              has been fully consumed. Depending on site policies, your electricity supply may be 
              limited until the next rationing period.
            </li>
          </ul>
          <h3 className="font-semibold mt-4 font-mont">Tips for Managing Your Electricity Usage</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>Turn off lights and appliances when not in use</li>
            <li>Use energy-efficient appliances</li>
            <li>Avoid running multiple high-consumption appliances simultaneously</li>
            <li>Keep track of your daily consumption with our dashboard tools</li>
          </ul>
        </div>
      </div>

      {/* Contact Support Section - Temporarily commented out
      <div className="card bg-white shadow-md rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Need Help?</h2>
        <p className="mb-4">
          If you have questions about electricity rationing or need assistance, please contact support:
        </p>
        <div className="flex space-x-4">
          <a 
            href="mailto:support@hooraypower.com" 
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-block"
          >
            Email Support
          </a>
          <a 
            href="tel:+27123456789" 
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded inline-block"
          >
            Call Helpline
          </a>
        </div>
      </div>
      */}
    </div>
  );
}

export default RationingPage; 